import { useState } from 'react';
import { API_CONFIG, HTTP_METHODS, CONTENT_TYPES } from '../config/api';

// Custom hook for authentication API calls
export const useAuthApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generic API call function
  const makeApiCall = async (endpoint, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const {
        method = HTTP_METHODS.GET,
        body,
        headers = {},
        baseUrl = API_CONFIG.BASE_URL,
      } = options;

      // Prepare headers
      const requestHeaders = {
        'Content-Type': CONTENT_TYPES.JSON,
        ...headers,
      };

      // Prepare request config
      const config = {
        method,
        headers: requestHeaders,
      };

      // Add body for non-GET requests
      if (body && method !== HTTP_METHODS.GET) {
        config.body = JSON.stringify(body);
      }

      const response = await fetch(`${baseUrl}${endpoint}`, config);
      
      // Safe JSON parsing
      const contentType = response.headers.get('content-type');
      let data = null;
      
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (text) {
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            console.error('JSON parse error:', parseError);
            data = { message: 'Invalid JSON response' };
          }
        } else {
          data = {};
        }
      } else {
        const text = await response.text();
        data = { message: text || 'No response data' };
      }

      if (response.ok) {
        return { success: true, data: data || {} };
      } else {
        const errorMessage = data?.message || `HTTP Error: ${response.status}`;
        setError(errorMessage);
        return { 
          success: false, 
          message: errorMessage, 
          errors: data?.errors || [] 
        };
      }
    } catch (err) {
      const errorMessage = err.message || 'Network error occurred';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Login API call
  const loginApi = async (email, password) => {
    const requestBody = {
      email,
      password
    };

    const result = await makeApiCall(API_CONFIG.ACCOUNT.LOGIN, {
      method: HTTP_METHODS.POST,
      body: requestBody
    });

    if (result.success) {
      // Handle different possible response structures
      const responseData = result.data;
      
      // Check if the response has the expected structure
      if (responseData) {
        return {
          success: true,
          token: responseData.token || responseData.accessToken || 'mock-token',
          user: responseData.user || responseData || {
            id: responseData.id || 1,
            firstName: responseData.firstName || responseData.name || 'User',
            lastName: responseData.lastName || '',
            email: email,
            role: responseData.role || 'Customer'
          },
          expiresAt: responseData.expiresAt || responseData.expires || null,
          data: responseData
        };
      }
    }

    return result;
  };

  // Register API call
  const registerApi = async (userData) => {
    const requestBody = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      gender: userData.gender, // "Male" | "Female"
      email: userData.email,
      phoneNumber: userData.phoneNumber, // with country code
      password: userData.password,
      confermPasswordHash: userData.confermPasswordHash,
      role: userData.role || 'Customer'
    };

    const result = await makeApiCall(API_CONFIG.ACCOUNT.REGISTER, {
      method: HTTP_METHODS.POST,
      body: requestBody
    });

    if (result.success) {
      // Expected response structure:
      // {
      //   "success": true,
      //   "userId": "number",
      //   "message": "Registration successful"
      // }
      return {
        success: true,
        userId: result.data.userId,
        message: result.data.message,
        data: result.data
      };
    } else {
      // Expected error response structure:
      // {
      //   "success": false,
      //   "message": "Error description",
      //   "errors": ["validation errors"]
      // }
      return {
        success: false,
        message: result.message,
        errors: result.errors || []
      };
    }
  };

  // Send OTP for registration email verification
  const sendRegisterOtpApi = async (email) => {
    const result = await makeApiCall(API_CONFIG.ACCOUNT.SEND_REGISTER_OTP, {
      method: HTTP_METHODS.POST,
      body: { email }
    });

    if (result.success) {
      return {
        success: true,
        message: result.data.message || 'OTP sent successfully',
        data: result.data
      };
    } else {
      return {
        success: false,
        message: result.message || 'Failed to send OTP',
        errors: result.errors || []
      };
    }
  };

  // Verify OTP for registration email verification
  const verifyRegisterOtpApi = async (email, otp) => {
    const result = await makeApiCall(API_CONFIG.ACCOUNT.VERIFY_REGISTER_OTP, {
      method: HTTP_METHODS.POST,
      body: { email, otp }
    });

    if (result.success) {
      return {
        success: true,
        message: result.data.message || 'OTP verified successfully',
        data: result.data
      };
    } else {
      return {
        success: false,
        message: result.message || 'Invalid or expired OTP',
        errors: result.errors || []
      };
    }
  };

  // Forgot Password API call
  const forgotPasswordApi = async (emailOrMobile, channel = "email") => {
    const requestBody = {
      emailOrMobile,
      channel
    };

    const result = await makeApiCall(API_CONFIG.ACCOUNT.FORGOT_PASSWORD, {
      method: HTTP_METHODS.POST,
      body: requestBody
    });

    if (result.success) {
      // Expected response structure:
      // {
      //   "success": true,
      //   "message": "Password reset email sent"
      // }
      return {
        success: true,
        message: result.data.message,
        data: result.data
      };
    } else {
      return {
        success: false,
        message: result.message,
        errors: result.errors || []
      };
    }
  };

  // Reset Password API call
  const resetPasswordApi = async (emailOrMobile, code, newPassword) => {
    const requestBody = {
      emailOrMobile,
      code,
      newPassword
    };

    const result = await makeApiCall(API_CONFIG.ACCOUNT.RESET_PASSWORD, {
      method: HTTP_METHODS.POST,
      body: requestBody
    });

    if (result.success) {
      // Expected response structure:
      // {
      //   "success": true,
      //   "message": "Password reset successful"
      // }
      return {
        success: true,
        message: result.data.message,
        data: result.data
      };
    } else {
      return {
        success: false,
        message: result.message,
        errors: result.errors || []
      };
    }
  };

  return {
    loading,
    error,
    loginApi,
    registerApi,
    sendRegisterOtpApi,
    verifyRegisterOtpApi,
    forgotPasswordApi,
    resetPasswordApi,
    makeApiCall
  };
};